import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { t } from '../../utils/translations';
import logo from '../../assets/images/logo.png';
import arabicFlag from '../../assets/images/arabic.png';
import englishFlag from '../../assets/images/english.png';

const LandingHeader: React.FC = () => {
  const [lang, setLang] = useState<string>(() => localStorage.getItem('locale') || 'en');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Apply direction whenever lang changes (covers initial render + switches)
  useEffect(() => {
    if (lang === 'ar') {
      document.body.setAttribute('dir', 'rtl');
      document.body.classList.add('rtl');
    } else {
      document.body.setAttribute('dir', 'ltr');
      document.body.classList.remove('rtl');
    }
  }, [lang]);

  const switchLanguage = (newLang: string) => {
    setLang(newLang);
    localStorage.setItem('locale', newLang);
    // Apply RTL to body
    if (newLang === 'ar') {
      document.body.setAttribute('dir', 'rtl');
      document.body.classList.add('rtl');
    } else {
      document.body.setAttribute('dir', 'ltr');
      document.body.classList.remove('rtl');
    }
    // Reload page to apply language change
    window.location.reload();
  };

  return (
    <div className="landing-header position-relative z-index-1 bg-base-gradient" data-kt-sticky="true" data-kt-sticky-name="landing-header" data-kt-sticky-offset="{default: '200px', lg: '300px'}">
      <div className="container">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center flex-equal">
            <button
              type="button"
              className="btn btn-icon btn-active-color-primary me-3 d-flex d-lg-none"
              id="kt_landing_menu_toggle"
              onClick={() => setMobileMenuOpen(prev => !prev)}
            >
              <span className="svg-icon svg-icon-2hx text-base">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 7H3C2.4 7 2 6.6 2 6V4C2 3.4 2.4 3 3 3H21C21.6 3 22 3.4 22 4V6C22 6.6 21.6 7 21 7Z" fill="currentColor" />
                  <path opacity="0.3" d="M21 14H3C2.4 14 2 13.6 2 13V11C2 10.4 2.4 10 3 10H21C21.6 10 22 10.4 22 11V13C22 13.6 21.6 14 21 14ZM22 20V18C22 17.4 21.6 17 21 17H3C2.4 17 2 17.4 2 18V20C2 20.6 2.4 21 3 21H21C21.6 21 22 20.6 22 20Z" fill="currentColor" />
                </svg>
              </span>
            </button>
            <Link to="/home" className="mx-5 mt-5">
              <img alt="Logo" src={logo} className="logo-default h-80px h-lg-80px" />
              <img alt="Logo" src={logo} className="logo-sticky h-50px h-lg-50px" />
            </Link>
          </div>
          {mobileMenuOpen && (
            <div className="landing-mobile-menu-overlay d-lg-none" onClick={() => setMobileMenuOpen(false)} />
          )}
          <div className="d-lg-block" id="kt_header_nav_wrapper">
            <div
              className={`d-lg-block p-5 p-lg-0${mobileMenuOpen ? ' landing-mobile-menu-open' : ''}`}
              id="kt_landing_menu_drawer"
            >
              <div className="menu menu-column flex-nowrap menu-rounded menu-lg-row menu-title-gray-800 menu-state-title-primary nav nav-flush fs-5 fw-semibold" id="kt_landing_menu">
                <div className="menu-item">
                  <Link className="menu-link nav-link py-3 px-4 px-xxl-6" to="/home" onClick={() => setMobileMenuOpen(false)}>{t('Home', lang)}</Link>
                </div>
                <div className="menu-item">
                  <Link
                    className="menu-link nav-link py-3 px-4 px-xxl-6"
                    to="/home#about"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('About Fesilah', lang)}
                  </Link>
                </div>
                <div className="menu-item">
                  <Link
                    className="menu-link nav-link py-3 px-4 px-xxl-6"
                    to="/home#features"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('Features', lang)}
                  </Link>
                </div>
                <div className="menu-item">
                  <Link
                    className="menu-link nav-link py-3 px-4 px-xxl-6"
                    to="/home#faqs"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('FAQS', lang)}
                  </Link>
                </div>
                <div className="menu-item">
                  <Link
                    className="menu-link nav-link py-3 px-4 px-xxl-6"
                    to="/home#download"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('Download App', lang)}
                  </Link>
                </div>
                <div className="menu-item">
                  <Link
                    className="menu-link nav-link py-3 px-4 px-xxl-6"
                    to="/home#contact"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('Contact Us', lang)}
                  </Link>
                </div>
                <div className="menu-item">
                  {lang === 'ar' ? (
                    <a href="#" onClick={(e) => { e.preventDefault(); switchLanguage('en'); }} className="border-right me-3 btn btn-icon btn-custom btn-icon-muted w-50px h-50px w-md-50px h-md-50px">
                      <img src={arabicFlag} width="50px" alt="eng" />
                    </a>
                  ) : (
                    <a href="#" onClick={(e) => { e.preventDefault(); switchLanguage('ar'); }} className="border-right me-3 btn btn-icon btn-custom btn-icon-muted w-50px h-50px w-md-50px h-md-50px">
                      <img src={englishFlag} width="50px" alt="arabic" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex-half text-end ms-1">
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingHeader;


